import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GetAccess, GetRole, CheckEditAction, CheckDeleteAction } from "../../../../Components/Utils/roles";
import { useDeleteData } from "../../../../Components/Hooks/useDeleteData";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import PremiumTable, { CopyButton } from "../../../../Components/Others/PremiumTable";
import { FaStar } from "react-icons/fa";

// Agent status badge color themes helper
const getAgentStatusTheme = (status) => {
  const s = String(status || "").trim().toLowerCase();
  if (s === "active") return "green";
  if (s === "in active" || s === "inactive") return "red";
  return "yellow";
};

const renderAgentStatus = (status) => {
  const theme = getAgentStatusTheme(status);

  const themes = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200/50 shadow-sm",
    yellow: "bg-amber-50 text-amber-700 border-amber-200/50 shadow-sm",
    red: "bg-rose-50 text-rose-700 border-rose-200/50 shadow-sm",
  };

  const colorClass = themes[theme] || themes.yellow;

  return (
    <span className={`inline-flex items-center text-center justify-center px-3.5 py-1 rounded-full text-[11px] font-bold border leading-snug tracking-wide max-w-[170px] ${colorClass}`}>
      {status || "N/A"}
    </span>
  );
};

// Ratings Stars Helper
const renderRatings = (ratings) => {
  const r = Number(ratings || 0);
  if (!r) return <span className="text-gray-400 text-xs">No Ratings</span>;
  return (
    <div className="flex items-center gap-0.5" title={`${r} Stars`}>
      {Array.from({ length: 5 }).map((_, idx) => (
        <FaStar
          key={idx}
          className={`w-3 h-3 ${idx < r ? "text-amber-400" : "text-gray-200"}`}
        />
      ))}
    </div>
  );
};

// Agents Table Row Component
const AgentsTableRow = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("agents_edit") || GetRole();
  const canDelete = GetAccess("agents_delete") || GetRole();

  const bgClass = "bg-white";
  const hoverClass = "hover:bg-white/80";

  const handleConfirmDelete = () => {
    handleDeleteData(`/agents/${row.agentId}`, setDeleteConfirmOpen);
  };

  return (
    <>
      <tr className={`${bgClass} ${hoverClass} transition-colors align-middle`}>
        {/* ID */}
        <td className="p-4 align-middle border-t border-b border-l border-gray-200/60 rounded-tl-xl rounded-bl-xl text-xs font-semibold text-gray-500">
          <div className="flex items-center">
            <span>{row.agentId || "N/A"}</span>
            <CopyButton text={row.agentId} />
          </div>
        </td>

        {/* Company Name */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <div className="flex items-center text-base font-semibold text-blue-600">
            <Link
              to={`/shipping/agents/view/${row.agentId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="hover:underline text-base text-gray-800"
            >
              {row.companyName || "N/A"}
            </Link>
            <CopyButton text={row.companyName} />
          </div>
          {row.ratings && (
            <div className="mt-1">
              {renderRatings(row.ratings)}
            </div>
          )}
        </td>

        {/* Contact Person */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs font-semibold text-gray-700">
            {row.cpName || "N/A"}
          </span>
          {row.cpDesigination && (
            <div className="text-[10px] text-gray-400 font-semibold mt-0.5">
              {row.cpDesigination}
            </div>
          )}
        </td>

        {/* Mobile */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-gray-700">
          {row.phone?.display || row.phone?.number || "-"}
        </td>

        {/* Email */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60 text-xs font-semibold text-blue-600">
          {row.email ? (
            <a href={`mailto:${row.email}`} className="hover:underline">
              {row.email}
            </a>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {/* Location */}
        <td className="p-4 align-middle border-t border-b border-gray-200/60">
          <span className="text-xs font-semibold text-gray-700">
            {row.city || "N/A"}
          </span>
          {row.country && (
            <div className="text-[10px] text-gray-400 font-semibold mt-0.5">
              {row.country}
            </div>
          )}
        </td>

        {/* Action Buttons */}
        <td className="p-4 text-center align-middle border-t border-b border-r border-gray-200/60 rounded-tr-xl rounded-br-xl">
          <div className="flex items-start justify-center gap-1.5">
            {/* View */}
            <Link
              to={`/shipping/agents/view/${row.agentId}`}
              state={{ data: row, pageIndex, searchQuery }}
              className="border border-blue-200 text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
              title="View details"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </Link>

            {/* Edit */}
            {CheckEditAction(row, "Agents", canEdit) ? (
              <Link
                to={`/shipping/agents/edit/${row.agentId}`}
                state={{ data: row, from: "/shipping/agents", pageIndex, searchQuery }}
                className="border border-sky-200 text-[#0ea5e9] hover:bg-sky-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Edit Agent"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </Link>
            ) : null}

            {/* Delete */}
            {CheckDeleteAction(row, canDelete) ? (
              <button
                onClick={() => setDeleteConfirmOpen(true)}
                className="border border-red-200 text-red-500 hover:bg-red-50 p-1.5 rounded-md transition-all duration-200 flex items-center justify-center"
                title="Delete Agent"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this Agent record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
};

// Agents Card Component
const AgentsCard = ({
  row,
  isSelected,
  onSelect,
  pageIndex,
  searchQuery,
  fetcher,
  index,
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { handle: handleDeleteData } = useDeleteData(fetcher);

  const canEdit = GetAccess("agents_edit") || GetRole();
  const canDelete = GetAccess("agents_delete") || GetRole();

  const handleConfirmDelete = () => {
    handleDeleteData(`/agents/${row.agentId}`, setDeleteConfirmOpen);
  };

  const theme = getAgentStatusTheme(row.status);

  // Card themes classes
  const themeStyles = {
    green: {
      borderLeft: "border-l-4 border-l-emerald-500",
      iconBg: "bg-emerald-50 text-emerald-600",
    },
    yellow: {
      borderLeft: "border-l-4 border-l-amber-500",
      iconBg: "bg-amber-50 text-amber-600",
    },
    red: {
      borderLeft: "border-l-4 border-l-rose-500",
      iconBg: "bg-rose-50 text-rose-600",
    }
  };

  const styles = themeStyles[theme] || themeStyles.yellow;
  const borderLeftClass = styles.borderLeft;
  const iconBgClass = styles.iconBg;

  return (
    <div className={`relative border border-y-gray-200/50 border-r-gray-200/50 border-l-0 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-4 flex flex-col overflow-hidden ${borderLeftClass}`}>
      {/* Top Header Row of Card */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-start  sm:gap-4 w-full">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-3 shrink-0">
            {/* Styled Avatar or First Letter */}
            <div className={`lg:w-12 w-10 lg:h-12 h-10 rounded-xl flex items-center justify-center font-bold text-lg select-none ${iconBgClass}`}>
              {row.image?.file ? (
                <img src={row.image.file} alt={row.companyName} className="w-full h-full object-cover rounded-xl" />
              ) : (
                row.companyName ? row.companyName[0].toUpperCase() : "?"
              )}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <Link
                  to={`/shipping/agents/view/${row.agentId}`}
                  state={{ data: row, pageIndex, searchQuery }}
                  className="text-gray-800 font-bold hover:underline text-[14px]  max-w-[140px]"
                  title={row.companyName}
                >
                  {row.companyName || "N/A"}
                </Link>
                <CopyButton text={row.companyName} />
              </div>
              <span className="text-[11px] text-gray-500 font-semibold mt-0.5">
                ID: {row.agentId || "N/A"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex sm:flex-col flex-row sm:justify-between sm:items-end items-center gap-2 w-full ">
          {/* Right Side: Status Badge */}
          <div className="shrink-0 scale-95 origin-right">
            {renderAgentStatus(row.status)}
          </div>
          {/* Ratings under header if present */}
          {row.ratings && (
            <div className="lg:mt-2.5 px-1">
              {renderRatings(row.ratings)}
            </div>
          )}
        </div>

      </div>



      {/* Middle Panel Grid */}
      <div className="rounded-xl py-2 flex lg:flex-row flex-col justify-between items-start lg:items-center sm:gap-1 gap-2 lg:mt-4 bg-gray-50/50 lg:px-4">
        {/* Contact Person */}
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Contact</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight truncate lg:max-w-[120px]" title={row.cpName}>
            {row.cpName || "N/A"}
          </span>
          {row.cpDesigination && (
            <span className="text-[10px] text-gray-400 truncate max-w-[120px]">{row.cpDesigination}</span>
          )}
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-200 hidden sm:block" />

        {/* Mobile & Email */}
        <div className="flex flex-col gap-0.5 flex-1 sm:pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Communication</span>
          <span className="text-[11px] font-bold text-gray-700 leading-tight">
            {row.phone?.display || row.phone?.number || "-"}
          </span>
          {row.email && (
            <span className="text-[10px] text-blue-600 truncate max-w-[120px]" title={row.email}>{row.email}</span>
          )}
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-200 hidden sm:block" />

        {/* Location */}
        <div className="flex flex-col gap-0.5 flex-1 sm:pl-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase select-none">Location</span>
          <span className="text-[12px] font-bold text-gray-700 leading-tight truncate max-w-[100px]" title={row.city}>
            {row.city || "N/A"}
          </span>
          {row.postalCode && (
            <span className="text-[10px] text-gray-400">{row.postalCode}</span>
          )}
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-4">
        {/* View Details */}
        <Link
          to={`/shipping/agents/view/${row.agentId}`}
          state={{ data: row, pageIndex, searchQuery }}
          className="border border-gray-200 text-gray-600 hover:bg-gray-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
          title="View details"
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="hidden sm:inline">View Details</span>
        </Link>

        {/* Edit & Delete */}
        <div className="flex items-center gap-1.5">
          {CheckEditAction(row, "Agents", canEdit) ? (
            <Link
              to={`/shipping/agents/edit/${row.agentId}`}
              state={{ data: row, from: "/shipping/agents", pageIndex, searchQuery }}
              className="border border-sky-100 text-[#0ea5e9] hover:bg-sky-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Edit"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </Link>
          ) : null}

          {CheckDeleteAction(row, canDelete) ? (
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="border border-red-100 text-red-500 hover:bg-red-50 px-2.5 sm:px-3.5 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
              title="Delete"
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* Delete Warning Modal */}
      {deleteConfirmOpen && (
        <ModalContainer
          visiable={deleteConfirmOpen}
          title="Warning"
          content={`Are you sure you want to remove this Agent record?`}
          handleClick={handleConfirmDelete}
          closeModal={() => setDeleteConfirmOpen(false)}
        />
      )}
    </div>
  );
};

// Main AgentsTable Component
const AgentsTable = ({
  data = [],
  total = 0,
  pageIndex,
  setPageIndex,
  dataCount,
  setDataCount,
  searchQuery,
  listofNumbers = [],
  selectOneFunction,
  selectMultipleFunction,
  fullList = [],
  fetcher,
  dataLoading = false,
  showEdit = false,
  setShowEdit,
  viewMode = "table",
}) => {
  // exact column header names as requested
  const headers = [
    { title: "ID" },
    { title: "Company Name" },
    { title: "Contact Person " },
    { title: "Mobile" },
    { title: " Email" },
    { title: "Location" },
    { title: "Action", align: "center" },
  ];

  return (
    <PremiumTable
      data={data}
      total={total}
      pageIndex={pageIndex}
      setPageIndex={setPageIndex}
      dataCount={dataCount}
      setDataCount={setDataCount}
      viewMode={viewMode}
      headers={headers}
      renderRow={(row, idx) => (
        <AgentsTableRow
          row={row}
          isSelected={listofNumbers.includes(row.agentId)}
          onSelect={() => selectOneFunction(row.agentId)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      renderCard={(row, idx) => (
        <AgentsCard
          row={row}
          isSelected={listofNumbers.includes(row.agentId)}
          onSelect={() => selectOneFunction(row.agentId)}
          pageIndex={pageIndex}
          searchQuery={searchQuery}
          fetcher={fetcher}
          index={idx}
        />
      )}
      dataLoading={dataLoading}
      listofNumbers={listofNumbers}
      selectMultipleFunction={selectMultipleFunction}
      fullList={fullList}
      showEdit={showEdit}
      setShowEdit={setShowEdit}
      multiEditLabel="Multi Edit"
    />
  );
};

export default AgentsTable;
